// 261010 P8c — bước 4 QUAY PHIM của một video: chạy lẻ (AI chữ) + sửa shot (EventShotCard dời từ bước 3). Không hiện giá. Hộp xác nhận chỉ khi ghi đè.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, message } from 'antd';
import { filmApi } from '../../services/api/film';
import type { EventPlan, FilmEvent } from '../../types/film';
import type { StyleProfileView } from '../../types/filmStyleProfile';
import { useAliveRef } from '../../hooks/useAliveRef';
import EventShotCard from './EventShotCard';
import WorkflowEditFrame, { type EditorApi } from './WorkflowEditShared';
import type { InteractiveProps } from './WorkflowStepShared';
import { castOf, diffToOps, lineTakenOf } from './workflowEditOps';
import { SHOT_STRUCT_CONFIRM, reorderIdsOf, shotMoveFlagsOf, shotStructDisabledReason } from './workflowStructOps';
import { apiErrorText } from './apiErrorText';
import { beatLinkText, subjectFacingLabel } from './workflowDetailUtils';
import { fmtSec, shotRowsOf } from './shotCameraUtils';
import { truncateText } from './workflowBoardUtils';
import { shotAnchorId, shotPositions } from './stepReviewPolicy';
import { ShotCueServe } from './ActingCueBlocks';
import {
  cameraRuleCardOf, cinematographyActionError, cinematographyBarOf, cinematographyConfirmContent, cinematographyNothingToDoOf, rhythmLineOf,
} from './cinematographyPolicy';
import type { ActionError } from './workflowActionPolicy';
import './acting.css';
import { needsConfirm } from './workflowConfirmPolicy';

interface Props extends InteractiveProps {
  eventId: string;
  ev: FilmEvent;
  detail: EventPlan | null;
}

const clamp2: React.CSSProperties = { display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };

/**
 * 261010 UI1b — bảng shot (mẫu v7 điểm 7): cỡ cảnh · góc máy · ống kính (mm SUY từ cỡ cảnh, chưa có trường thật) · chuyển động máy · thời lượng · mục đích · ghi chú / nhịp / thoại.
 * Chỉ đọc; sửa shot vẫn qua `EventShotCard` ở chế độ Sửa. Giữ hướng mặt chủ thể + nhịp truyện + cue diễn xuất của P8c trong ô cuối.
 */
const CinematographyView: React.FC<{ ev: FilmEvent }> = ({ ev }) => {
  const shots = ev.shots || [];
  if (!shots.length) return <p className="muted" style={{ margin: '6px 0' }}>Chưa có shot nào — dùng «Dựng theo diễn xuất» ở bước 3 hoặc chặng 2 của Máy tự chạy.</p>;
  const pos = shotPositions(shots);
  const rows = shotRowsOf(shots);
  const byId = new Map(shots.map((s) => [s.id, s]));
  const total = rows.reduce((n, r) => n + (r.durationS ?? 0), 0);
  return (
    <div data-testid="wf-shot-table">
      <p className="wf-note" style={{ margin: '0 0 8px' }}>{rows.length} shot{total > 0 ? ` · ${fmtSec(total)} giây` : ''} · ống kính (mm) suy từ cỡ cảnh, chỉ để hình dung.</p>
      <div className="wf-ctab" role="table" aria-label="Bảng shot">
        <div className="wf-ctab-h" role="row">
          <span role="columnheader">#</span><span role="columnheader">Cỡ cảnh</span><span role="columnheader">Góc máy</span><span role="columnheader">Ống kính</span>
          <span role="columnheader">Chuyển động máy</span><span role="columnheader">Thời lượng</span><span role="columnheader">Mục đích</span><span role="columnheader">Ghi chú · nhịp · thoại</span>
        </div>
        {rows.map((r) => {
          const s = byId.get(r.key)!;
          const beat = beatLinkText(s.beatOrders, ev.beatMeta);
          return (
            <div key={r.key} className="wf-ctab-r" role="row" id={shotAnchorId(pos.get(r.key) ?? r.no)}>
              <span className="mono" role="cell"><b>S{r.no}</b></span>
              <span role="cell"><b>{r.sizeCode || '—'}</b>{r.sizeEn ? <><br /><span className="mono faint">{r.sizeEn}</span></> : null}</span>
              <span role="cell">{r.angleLabel || '—'}</span>
              <span className="mono" role="cell">{r.mm != null ? `${r.mm}mm` : '—'}</span>
              <span role="cell">{r.moveCode || '—'}{r.moveEn ? <><br /><span className="mono faint">{r.moveEn}</span></> : null}</span>
              <span className="mono" role="cell">{r.durationS != null ? <b>{fmtSec(r.durationS)} s</b> : '—'}</span>
              <span role="cell">{r.purpose ? <span className="wf-chip todo">{r.purpose}</span> : '—'}</span>
              <span role="cell" className="wf-ctab-why">
                <span>Hướng mặt: <b>{subjectFacingLabel(s.screenDirection)}</b></span>
                {beat ? <span title={beat} style={clamp2}>Nhịp truyện: {beat}</span> : null}
                {r.note ? <span className="muted" title={r.note} style={clamp2}>{r.note}</span> : null}
                {r.dialogue.map((d, i) => <span key={i} className="wf-ctab-dl">🗣 {truncateText(d, 60)}</span>)}
                <ShotCueServe shot={s} plan={ev.actingPlan} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WorkflowCinematographyStep: React.FC<Props> = (props) => {
  const { planId, eventId, ev, detail, stepState, jobs, failures, onActionDone, onJobsRefresh, onGoStep } = props;
  const alive = useAliveRef();
  const [style, setStyle] = useState<StyleProfileView | null>(null);
  const [sending, setSending] = useState(false);
  const [runErr, setRunErr] = useState<ActionError | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [structBusy, setStructBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    filmApi.getStyleProfile(planId).then((r) => { if (!cancelled && alive.current) setStyle(r); }).catch(() => { /* thẻ luật nói rõ */ });
    return () => { cancelled = true; };
  }, [planId, alive]);

  const fail = (failures || []).find((f) => f.type === 'FILM_DIRECT' && f.phase === 'cinematography-event' && (!f.eventId || f.eventId === eventId));
  const hasPromptOrClip = !!ev.authorPrompt || (ev.clips || []).length > 0;
  const rules = useMemo(() => cameraRuleCardOf(style), [style]);
  const rhythm = useMemo(() => rhythmLineOf(ev, style), [ev, style]);

  const doRun = useCallback(async (force: boolean, from: 'NOT_READY' | 'ERROR') => {
    setRunErr(null); setNote(null); setSending(true);
    try {
      const r = await filmApi.startCinematography(planId, eventId, force);
      if (!alive.current) return;
      if (r && r.started === false && r.reason === 'NOTHING_TO_DO') {
        const follow = cinematographyNothingToDoOf(from);
        if (follow.kind === 'confirm') {
          // U9: chạy lại quay phim = chạy lại một bước (không phải clip video) ⇒ chạy ngay, không hộp xác nhận.
          if (needsConfirm('CINEMATOGRAPHY', 'RERUN')) { const c = cinematographyConfirmContent(ev.eventOrder, hasPromptOrClip); Modal.confirm({ title: c.title, content: c.body, okText: c.okText, cancelText: c.cancelText, onOk: () => { void doRun(true, from); } }); }
          else void doRun(true, from);
        } else {
          setNote(follow.text);
          onActionDone();
        }
        return;
      }
      await onJobsRefresh();
      onActionDone();
    } catch (e: any) {
      if (alive.current) setRunErr(cinematographyActionError(e, 'Chạy quay phim'));
    } finally { if (alive.current) setSending(false); }
  }, [planId, eventId, ev.eventOrder, hasPromptOrClip, alive, onActionDone, onJobsRefresh]);

  const extra = ({ editing, dirty }: { editing: boolean; dirty: boolean }) => {
    const bar = cinematographyBarOf({
      eventId, eventOrder: ev.eventOrder, stepState, shots: ev.shots || [], jobs, sending, editing, dirty, errorNote: fail?.note,
    });
    const onClick = () => {
      if (!bar.button.enabled) return;
      if (bar.button.confirm) {
        if (needsConfirm('CINEMATOGRAPHY', 'RERUN')) { const c = cinematographyConfirmContent(ev.eventOrder, hasPromptOrClip); Modal.confirm({ title: c.title, content: c.body, okText: c.okText, cancelText: c.cancelText, onOk: () => { void doRun(true, 'NOT_READY'); } }); }
        else void doRun(true, 'NOT_READY'); // U9: chạy lại ngay
        return;
      }
      void doRun(bar.button.force, stepState === 'ERROR' ? 'ERROR' : 'NOT_READY');
    };
    return (
      <div className="wf-act" data-testid="wf-cine-bar" style={{ marginBottom: 10 }}>
        {runErr ? <p className="wf-alert ERROR" data-testid="wf-cine-run-error">{runErr.text} <button type="button" className="wf-btn ghost" onClick={() => setRunErr(null)}>Đóng</button></p> : null}
        {note ? <p className="wf-note" data-testid="wf-cine-nothing">{note}</p> : null}
        <div className="wf-act-bar">
          <span className={`wf-chip ${bar.chip.tone}`} data-testid="wf-cine-chip">{bar.chip.label}</span>
          <Button size="small" type={bar.button.confirm ? 'default' : 'primary'} loading={sending} disabled={!bar.button.enabled}
            title={bar.button.enabled ? 'Ghi hướng mặt chủ thể + nhịp truyện; không đổi cỡ/góc/máy' : bar.button.reason}
            onClick={onClick} data-testid="wf-cine-run">{bar.button.label}</Button>
          {bar.button.goDirecting && onGoStep ? <Button size="small" onClick={() => onGoStep('DIRECTING')}>Sang bước 3</Button> : null}
          {bar.button.reason && !bar.button.enabled ? <span className="muted" style={{ fontSize: 13 }}>{bar.button.reason}</span> : null}
        </div>
        <p className="wf-note" style={{ margin: 0 }} title={rhythm}>{rhythm}</p>
        <div className="wf-act-sec">
          <h4>Luật vai Quay phim</h4>
          {rules.note ? <p className="wf-note" style={{ margin: '0 0 6px' }}>{rules.note}</p> : null}
          {rules.rules.length > 0 && (
            <ul className="wf-act-rules">{rules.rules.map((r) => (
              <li key={r.id}><b>{r.title}</b><span className="own">{r.ownerText}</span> — {r.when} → {r.then}</li>
            ))}</ul>
          )}
        </div>
      </div>
    );
  };

  const renderEditor = ({ draft, update, base, bank, saving, reopen }: EditorApi) => {
    const names = Array.from(new Set([...(bank?.characters || detail?.refBank?.characters || []).map((c) => c.name), ...castOf(base.shots)]));
    const taken = lineTakenOf(draft.shots);
    const pos = shotPositions(draft.shots);
    const allOps = diffToOps(base, draft);
    const changed = new Set(allOps.flatMap((o) => (o.type === 'updateShot' ? [o.shotId] : [])));
    // P9D D6 (SH8 SH9 SH14) — ↑ ↓ xoá thêm shot: gọi THẲNG route có sẵn (nháp `workflowEditOps` không mang op cấu trúc), rồi nạp lại bản gốc. Tắt khi còn nháp chưa lưu.
    const off = shotStructDisabledReason({ dirty: allOps.length > 0, busy: structBusy || saving });
    const structRun = async (what: string, fn: () => Promise<unknown>, done: string) => {
      setStructBusy(true);
      try { await fn(); message.success(done); }
      catch (e) { message.error(apiErrorText(e, what)); }
      finally { setStructBusy(false); await reopen(); props.onActionDone(); }
    };
    const confirmStruct = (k: 'delete' | 'add', fn: () => Promise<unknown>, what: string, done: string) => {
      const c = SHOT_STRUCT_CONFIRM[k];
      if (!needsConfirm('CINEMATOGRAPHY', k === 'delete' ? 'DELETE' : 'INSERT')) { void structRun(what, fn, done); return; } // U9: thêm shot trống không hỏi; xoá vẫn hỏi
      Modal.confirm({ title: c.title, content: c.content, okText: c.okText, cancelText: 'Huỷ', okButtonProps: k === 'delete' ? { danger: true } : undefined, onOk: () => structRun(what, fn, done) });
    };
    return (
      <div>
        <h3>Shot</h3>
        {draft.shots.length === 0 ? <p className="muted">Chưa có shot nào — dùng «Dựng theo diễn xuất» ở bước 3 hoặc chặng 2 của Máy tự chạy, hoặc thêm tay bên dưới.</p> : null}
        {draft.shots.map((s, i) => {
          const mv = shotMoveFlagsOf(draft.shots, i);
          return (
            <div key={s.id} id={shotAnchorId(pos.get(s.id) ?? s.orderIndex)}>
              <EventShotCard shot={s} cast={names} dirty={changed.has(s.id)} lines={draft.lines} lineTaken={taken}
                onSave={(id, patch) => { if (!saving) update((d) => { const sh = d.shots.find((x) => x.id === id); if (sh) Object.assign(sh, patch); }); }}
                canUp={!off && mv.canUp} canDown={!off && mv.canDown}
                onMove={(id, dir) => { const ids = reorderIdsOf(draft.shots, id, dir); if (ids) void structRun('Đổi thứ tự shot', () => filmApi.reorderEventShots(eventId, ids), 'Đã đổi thứ tự shot.'); }}
                onDelete={off ? undefined : (id) => confirmStruct('delete', () => filmApi.deleteEventShot(id), 'Xoá shot', 'Đã xoá shot.')} />
            </div>
          );
        })}
        <Button size="small" type="dashed" disabled={!!off} title={off ?? undefined} data-testid="wf-shot-add" style={{ marginTop: 6 }}
          onClick={() => confirmStruct('add', () => filmApi.addEventShot(eventId), 'Thêm shot', 'Đã thêm shot.')}>＋ Thêm shot</Button>
        {off ? <p className="faint" style={{ fontSize: 12, margin: '4px 0 0' }}>{off}</p> : null}
      </div>
    );
  };

  return (
    <WorkflowEditFrame
      {...props} stepKey="CINEMATOGRAPHY" extra={extra}
      view={(e) => <CinematographyView ev={e} />}
      renderEditor={renderEditor}
    />
  );
};

export default WorkflowCinematographyStep;
export { CinematographyView };
